/**
 * Cue resolution (AD-861, docs/architecture/client-audio.md §5): which cue id a game moment plays,
 * read from the cue map data (content/audio/cues.json → cues.generated.json). Pure: no Web Audio.
 * A moment the map does not name resolves to null and is silent — by design, never an error.
 */
import type { GameFeedbackEvent } from '../feedback/feedbackBus.js';

export type MusicState = 'yard' | 'combat' | 'boss' | 'victory';

export interface SfxCueDef {
  bus: 'sfx' | 'voice';
  gain: number;
  /** Served file ids (variants, picked round-robin); absent for a code-synthesised cue. */
  files?: string[];
  synth?: 'tap' | 'open' | 'close';
}

export interface MusicCueDef {
  file: string;
  loop: boolean;
  gain: number;
}

export interface EnemyCueDef {
  telegraph?: string;
  land?: string;
  music?: MusicState;
}

export interface CueMap {
  formats: string[];
  sfx: Record<string, SfxCueDef>;
  music: Record<string, MusicCueDef>;
  musicStates: Record<MusicState, string>;
  feedback: Record<string, string>;
  abilityFeel: Record<string, string>;
  abilities: Record<string, string>;
  gatherSkill: Record<string, string>;
  preload: Record<string, string[]>;
  /** Keyed by the enemy's on-the-wire display NAME (the build resolves content ids). */
  enemies: Record<string, EnemyCueDef>;
}

/** Context the resolver needs that the event does not carry. */
export interface ResolveContext {
  /** `gatheringSkill` of a resource content id (from the content pack), if known. */
  skillOf?: (resourceId: string) => string | undefined;
}

const known = (map: CueMap, id: string | undefined): string | null =>
  id !== undefined && map.sfx[id] ? id : null;

/** The SFX cue a feedback-bus event plays, or null (silent). */
export function cueForFeedback(
  map: CueMap,
  ev: GameFeedbackEvent,
  ctx: ResolveContext = {},
): string | null {
  const f = map.feedback;
  switch (ev.kind) {
    case 'weaponHit':
      return known(map, ev.heavy ? (f['weaponHit.heavy'] ?? f.weaponHit) : f.weaponHit);
    case 'playerDamaged':
      return known(map, f.playerDamaged);
    case 'dodge':
      return known(map, f.dodge);
    case 'ability':
      return (
        known(map, map.abilities[ev.abilityId]) ??
        (ev.feel ? known(map, map.abilityFeel[ev.feel]) : null)
      );
    case 'gatherStrike': {
      const skill = ctx.skillOf?.(ev.resource);
      return skill ? known(map, map.gatherSkill[skill]) : null;
    }
    case 'gather':
      return known(map, ev.ok ? f['gather.ok'] : f['gather.fail']);
    case 'craft':
      return known(map, ev.ok ? f['craft.ok'] : f['craft.fail']);
    case 'loot':
      return known(map, ev.rare ? (f['loot.rare'] ?? f.loot) : f.loot);
    case 'questComplete':
      return known(map, f.questComplete);
    case 'objectiveComplete':
      return known(map, f.objectiveComplete);
    case 'questReady':
      return known(map, f.questReady);
    case 'levelUp':
      return known(map, f.levelUp);
    case 'died':
      return known(map, ev.self ? f['died.self'] : f['died.other']);
    case 'respawned':
      return known(map, ev.self ? f['respawned.self'] : f['respawned.other']);
    default:
      // telegraphStart / telegraphLand carry no enemy identity: enemy cues come from the raw
      // telegraph events, by name (audioDirector.noteGameEvent), so they never double-fire.
      return null;
  }
}

/** The enemy-specific cue for a telegraph phase, by display name, or null. */
export function cueForEnemy(
  map: CueMap,
  name: string | undefined,
  phase: 'telegraph' | 'land',
): string | null {
  if (!name) return null;
  return known(map, map.enemies[name]?.[phase]);
}

/** True when this enemy name drives the boss music state. */
export function isBossName(map: CueMap, name: string | undefined): boolean {
  return !!name && map.enemies[name]?.music === 'boss';
}

/** The cue ids to fetch on entering a zone (its own list, else the default). */
export function preloadFor(map: CueMap, zoneId: string | null | undefined): string[] {
  return (zoneId ? map.preload[zoneId] : undefined) ?? map.preload.default ?? [];
}

/** The served URL of a file id for the chosen format (`/audio/<kind>/<id>.<ext>`). */
export function fileUrl(
  kind: 'sfx' | 'music' | 'voice',
  file: string,
  ext: string,
  base = '/audio',
): string {
  return `${base}/${kind}/${file}.${ext}`;
}
